/**
 * Authentication Service with MFA Support
 * Uses proxy through GridPilot backend to MaaS API
 */
import { AuthState, LoginResult, MfaResult, MfaState } from '../types/auth';
import { config } from '../config';

const STORAGE_KEY_TOKEN = 'auth_token';
const STORAGE_KEY_REFRESH_TOKEN = 'auth_refresh_token';
const STORAGE_KEY_TENANT = 'auth_tenant';
const STORAGE_KEY_TOKEN_EXPIRY = 'auth_token_expiry';

// MFA state (not persisted to storage)
let mfaState: MfaState = {
  required: false,
  ticket: null,
  expiresIn: null,
  availableMethods: [],
};

/**
 * JWT payloads are base64url and UTF-8: a plain atob() yields Latin-1 code units, which turned
 * "Schröder" into "SchrÃ¶der" in the header.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

export const authService = {
  /**
   * Login with username and password
   * Returns success: true if logged in, or MFA info if MFA is required
   */
  async login(username: string, password: string): Promise<LoginResult> {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const result = await response.json().catch(() => ({}));

      // Check if MFA is required (backend uses snake_case)
      const mfaTicket = result.mfaTicket || result.mfa_ticket;
      const mfaRequired = result.requiresMfa === true || result.error === 'mfa_required' || !!mfaTicket;

      if (mfaRequired && mfaTicket) {
        const expiresIn = result.expiresIn ?? result.mfa_expires_in ?? 180;
        const availableMethods = result.availableMfaMethods || result.available_mfa_methods || ['pin'];

        mfaState = {
          required: true,
          ticket: mfaTicket,
          expiresIn: expiresIn,
          availableMethods: availableMethods as ('pin' | 'totp')[],
        };

        return {
          success: false,
          requiresMfa: true,
          mfaTicket: mfaTicket,
          mfaExpiresIn: expiresIn,
          availableMfaMethods: mfaState.availableMethods,
        };
      }

      // If response was not OK and not MFA, return error
      if (!response.ok) {
        const errorMessage = this.translateError(result.error || result.error_description || `Login failed: ${response.status}`);
        return { success: false, error: errorMessage };
      }

      // Login successful without MFA
      const accessToken = result.accessToken || result.access_token;
      if (!accessToken) {
        return { success: false, error: 'Login response missing access token' };
      }

      this.storeTokens(result);
      return { success: true };
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      return { success: false, error: 'Verbindungsfehler zum Server' };
    }
  },

  /**
   * Complete MFA with PIN
   */
  async completeMfaPin(pin: string): Promise<MfaResult> {
    if (!mfaState.required || !mfaState.ticket) {
      return { success: false, error: 'Keine MFA-Anforderung aktiv' };
    }

    try {
      const response = await fetch('/api/auth/login/mfa/pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mfaTicket: mfaState.ticket,
          pin: pin,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage = this.translateError(result.error || result.error_description || 'MFA fehlgeschlagen');
        return { success: false, error: errorMessage };
      }

      const accessToken = result.accessToken || result.access_token;
      if (!accessToken) {
        return { success: false, error: 'MFA response missing access token' };
      }

      this.clearMfaState();
      this.storeTokens(result);
      return { success: true };
    } catch (error) {
      console.error('[AuthService] MFA PIN error:', error);
      return { success: false, error: 'Verbindungsfehler zum Server' };
    }
  },

  /**
   * Complete MFA with TOTP (Authenticator app)
   */
  async completeMfaTotp(code: string): Promise<MfaResult> {
    if (!mfaState.required || !mfaState.ticket) {
      return { success: false, error: 'Keine MFA-Anforderung aktiv' };
    }

    try {
      const response = await fetch('/api/auth/login/mfa/totp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mfaTicket: mfaState.ticket,
          totp: code,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage = this.translateError(result.error || result.error_description || 'MFA fehlgeschlagen');
        return { success: false, error: errorMessage };
      }

      const accessToken = result.accessToken || result.access_token;
      if (!accessToken) {
        return { success: false, error: 'MFA response missing access token' };
      }

      this.clearMfaState();
      this.storeTokens(result);
      return { success: true };
    } catch (error) {
      console.error('[AuthService] MFA TOTP error:', error);
      return { success: false, error: 'Verbindungsfehler zum Server' };
    }
  },

  /**
   * Get current MFA state
   */
  getMfaState(): MfaState {
    return { ...mfaState };
  },

  /**
   * Clear MFA state
   */
  clearMfaState(): void {
    mfaState = {
      required: false,
      ticket: null,
      expiresIn: null,
      availableMethods: [],
    };
  },

  /**
   * Store tokens from login/MFA response
   */
  storeTokens(result: any): void {
    const accessToken = result.accessToken || result.access_token;
    const refreshToken = result.refreshToken || result.refresh_token;
    const expiresIn = result.expiresIn || result.expires_in;
    const expiresAt = result.expiresAt || result.expires_at;

    if (accessToken) {
      localStorage.setItem(STORAGE_KEY_TOKEN, accessToken);
    }

    localStorage.setItem(STORAGE_KEY_TENANT, config.actingTenant);

    if (refreshToken) {
      localStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, refreshToken);
    }

    // Handle expiry - either expiresIn (seconds) or expiresAt (ISO date string)
    if (expiresIn) {
      const expiry = Date.now() + expiresIn * 1000;
      localStorage.setItem(STORAGE_KEY_TOKEN_EXPIRY, expiry.toString());
    } else if (expiresAt) {
      const expiry = new Date(expiresAt).getTime();
      localStorage.setItem(STORAGE_KEY_TOKEN_EXPIRY, expiry.toString());
    }
  },

  /**
   * Logs out user by clearing tokens
   */
  logout(): void {
    this.clearAuth();
    this.clearMfaState();
    window.location.reload();
  },

  /**
   * Gets the stored access token, checking for expiry
   */
  getToken(): string | null {
    if (typeof window === 'undefined') return null;

    const expiry = localStorage.getItem(STORAGE_KEY_TOKEN_EXPIRY);
    const token = localStorage.getItem(STORAGE_KEY_TOKEN);
    const expiryNum = expiry ? parseInt(expiry, 10) : null;
    const isExpired = expiryNum ? Date.now() > expiryNum : false;

    if (expiry && isExpired) {
      this.clearAuth();
      return null;
    }

    return token;
  },

  /**
   * Gets the tenant ID
   */
  getTenantId(): string | null {
    return config.actingTenant || localStorage.getItem(STORAGE_KEY_TENANT);
  },

  /**
   * Gets the current authentication state
   */
  getAuthState(): AuthState {
    const token = this.getToken();
    const tenantId = this.getTenantId();
    return {
      token,
      tenantId,
      isAuthenticated: !!token,
    };
  },

  /**
   * Checks if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  /**
   * Clears all auth data from storage
   */
  clearAuth(): void {
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEY_TENANT);
    localStorage.removeItem(STORAGE_KEY_TOKEN_EXPIRY);
  },

  /**
   * Translate error messages to German
   */
  translateError(error: string): string {
    const translations: Record<string, string> = {
      'invalid_credentials': 'Ungültiger Benutzername oder Passwort',
      'invalid_grant': 'Ungültiger Benutzername oder Passwort',
      'user_inactive': 'Ihr Konto ist deaktiviert',
      'user_locked': 'Ihr Konto ist gesperrt',
      'mfa_locked': 'Zu viele fehlgeschlagene MFA-Versuche',
      'invalid_pin': 'Ungültige PIN',
      'invalid_totp': 'Ungültiger Authenticator-Code',
      'invalid_mfa_ticket': 'MFA-Zeitfenster abgelaufen. Bitte erneut anmelden.',
      'role_required': 'Ihr Benutzer ist für diese GridPilot-Instanz nicht freigegeben.',
    };

    if (translations[error]) {
      return translations[error];
    }

    for (const [key, value] of Object.entries(translations)) {
      if (error.includes(key)) {
        return value;
      }
    }

    return error;
  },

  /**
   * Decodes JWT payload and extracts the username claim
   */
  getUsername(): string | null {
    const token = localStorage.getItem(STORAGE_KEY_TOKEN);
    if (!token) return null;
    try {
      const payload = decodeJwtPayload(token);
      // .NET JWT: ClaimTypes.Name = "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
      const name = payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']
        ?? payload['name']
        ?? payload['sub'];
      return typeof name === 'string' ? name : null;
    } catch {
      return null;
    }
  },

  /**
   * Check if authentication is enabled or in dev mode
   */
  async checkAuthStatus(): Promise<{ authEnabled: boolean; devMode: boolean }> {
    try {
      const response = await fetch('/api/auth/status');
      if (response.ok) {
        return await response.json();
      }
      // Default to auth enabled if status check fails
      return { authEnabled: true, devMode: false };
    } catch {
      // Default to auth enabled if network error
      return { authEnabled: true, devMode: false };
    }
  },
};
